import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Dialog,
    Grid,
    Typography,
    useTheme,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip
} from '@mui/material';
import { AddCircle, ArrowBack, ExpandMore, Visibility, InfoOutlined } from '@mui/icons-material';
import { Product, ProductImage } from '../../../../../models/DB';
import { BasicButton } from '../../../../../components/BasicButton';
import { BasicTextField } from '../../../../../components/BasicTextField';
import { productStore } from '../../../../../app/stores/Product';
import PortalService from '../../../../../services/supabase/PortalService';
import { BasicAutocomplete } from '../../../../../components/BasicAutocomplete';
import { calculateDiscountedPrice } from '../../../../../utils/customFunctions';
import { useStoreConfig } from '../../../../../contexts/StoreConfigContext';
import { DragDropImageUploader, ImageFile } from '../../../../../components/DragDropImageUploader';
import { ProductItem } from '../../../../../components/ProductItem';

interface AddProductFormProps {
    products: Product[];
    onClose: () => void;
}

export const AddProductForm: React.FC<AddProductFormProps> = ({ products, onClose }) => {
    const { createProduct } = PortalService();
    const { updateProducts } = productStore(state => state);
    const theme = useTheme();
    const { config } = useStoreConfig();

    const [productData, setProductData] = useState<Partial<Product>>({
        name: '',
        category: '',
        description: '',
        price: 0,
        stockOnHand: 0,
        discount: 0,
        discountType: 'percentage',
        inStock: true,
        isActive: true,
        showRating: true,
        variantType: '',
        variantOptions: [],
        images: []
    });

    const [productImages, setProductImages] = useState<ImageFile[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [uniqueCategories, setUniqueCategories] = useState<string[]>([]);
    const [uniqueVariants, setUniqueVariants] = useState<string[]>([]);
    const [uniqueVariantOptions, setUniqueVariantOptions] = useState<string[]>([]);

    // Get unique categories and variants from the products
    useEffect(() => {
        const categories = new Set<string>();
        const variants = new Set<string>();

        products.forEach(product => {
            if (product.category) {
                categories.add(product.category);
            }
            if (product.variantType) {
                variants.add(product.variantType);
            }
        });

        setUniqueCategories(Array.from(categories));
        setUniqueVariants(Array.from(variants));
    }, [products]);

    // Get unique variant options from the products
    useEffect(() => {
        if (productData.variantType) {
            const options = new Set<string>();
            products.forEach(product => {
                if (product.variantType === productData.variantType) {
                    product.variantOptions.forEach(option => options.add(option));
                }
            });
            setUniqueVariantOptions(Array.from(options));
        } else {
            setUniqueVariantOptions([]);
        }
    }, [productData.variantType, products]);

    const handleImageChange = useCallback((images: ImageFile[]) => {
        setProductImages(images);
    }, []);

    const handleSubmit = async () => {
        const trimTrailingWhitespace = (data: Partial<Product>) => {
            return Object.fromEntries(
                Object.entries(data).map(([key, value]) =>
                    typeof value === 'string' ? [key, value.replace(/\s+$/, '')] : [key, value]
                )
            );
        };

        const trimmedProductData = trimTrailingWhitespace(productData);

        // Build images array and collect files
        const images: ProductImage[] = [];
        const imageFiles: File[] = [];
        const newImageResizeModes: Record<string, string> = {};

        productImages.forEach((img, index) => {
            if (img.file) {
                const imageType = img.isThumbnail ? 'thumbnail' : `image${index}`;
                images.push({ type: imageType, url: '' });
                imageFiles.push(new File([img.file], imageType, { type: img.file.type }));
                if (img.resizeMode) {
                    newImageResizeModes[imageType] = img.resizeMode;
                }
            }
        });

        setLoading(true);
        const product = await createProduct(
            { ...trimmedProductData, images } as Product,
            imageFiles,
            newImageResizeModes
        );
        setLoading(false);

        if (product) {
            onClose();
            setTimeout(() => {
                updateProducts([...products, product]);
            }, 500);
        } else {
            console.error('Error creating product');
        }
    };

    const accordionSx = {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        mb: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: 0, mb: 2 }
    };

    const accordionSummarySx = {
        minHeight: 48,
        '&.Mui-expanded': { minHeight: 48 },
        px: 2.5
    };

    return (
        <Dialog
            open
            fullScreen
            onClose={onClose}
            PaperProps={{
                sx: {
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5'
                }
            }}
        >
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {/* Header */}
                <Box
                    sx={{
                        px: 3,
                        py: 1.5,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <BasicButton
                            title='Back to List'
                            variant='outlined'
                            onClick={onClose}
                            startIcon={<ArrowBack fontSize='small' />}
                            sx={{
                                height: 36,
                                fontSize: '0.8125rem',
                                px: 2,
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.primary,
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.05)'
                                        : 'rgba(0, 0, 0, 0.02)'
                                }
                            }}
                        />
                        <Typography
                sx={{
                                fontFamily: 'primaryFont',
                                fontSize: '1rem',
                                fontWeight: 600,
                                letterSpacing: 0.5
                }}
            >
                Add New Product
                        </Typography>
                    </Box>
                    <BasicButton
                        title='Create Product'
                        variant='contained'
                        onClick={handleSubmit}
                        isLoading={loading}
                        startIcon={<AddCircle fontSize='small' />}
                        sx={{
                            height: 36,
                            fontSize: '0.8125rem',
                            px: 3,
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark
                            }
                        }}
                        disabled={!productData.name || !productData.category || !productData.price}
                    />
                </Box>

                {/* Content Area */}
                <Box maxWidth='lg' sx={{ mb: 3, mx: 'auto' }}>
                    <Grid container spacing={2.5} justifyContent='center'>
                        {/* Column 1 - Live Preview & Product Media */}
                        <Grid item xs={12} md={5} sx={{ position: { md: 'sticky' }, top: { md: 16 }, alignSelf: 'flex-start' }}>
                            {/* Live Preview Card */}
                            <Paper
                                elevation={0}
                                sx={{
                                    backgroundColor: theme.palette.background.paper,
                                    borderRadius: 2,
                                    p: 3,
                                    mb: 2,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Visibility fontSize='small' sx={{ color: theme.palette.primary.main }} />
                                    <Typography
                                        variant='body2'
                                        sx={{
                                            fontWeight: 600,
                                            fontFamily: 'primaryFont',
                                            color: theme.palette.text.primary,
                                            textAlign: 'left'
                                        }}
                                    >
                                        Live Preview
                                    </Typography>
                                </Box>
                                <Typography
                                    variant='caption'
                                    sx={{
                                        display: 'block',
                                        mb: 2,
                                        fontFamily: 'primaryFont',
                                        color: theme.palette.text.secondary,
                                        textAlign: 'left'
                                    }}
                                >
                                    This is how your product will appear in the catalog
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        py: 2,
                                        px: 1,
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.02)'
                                            : 'rgba(0,0,0,0.02)',
                                        borderRadius: 2,
                                        border: `1px dashed ${theme.palette.divider}`
                                    }}
                                >
                                    <ProductItem
                                        previewMode
                                        width={180}
                                        nameOverride={productData.name || 'Product Name'}
                                        priceOverride={productData.price || 0}
                                        discountOverride={productData.discount}
                                        discountTypeOverride={productData.discountType}
                                        thumbnailUrlOverride={productImages.find(img => img.isThumbnail)?.preview}
                                        thumbnailResizeModeOverride={productImages.find(img => img.isThumbnail)?.resizeMode}
                                        inStockOverride={productData.inStock}
                                        showRatingOverride={productData.showRating}
                                        fontFamilyOverride={config.primaryFont ? `'${config.primaryFont}', sans-serif` : undefined}
                                    />
                                </Box>
                            </Paper>

                            {/* Product Media Card */}
                            <Paper
                                elevation={0}
                                sx={{
                                    backgroundColor: theme.palette.background.paper,
                                    borderRadius: 2,
                                    p: 3,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)'
                                }}
                            >
                                <Typography
                                    variant='body2'
                                    sx={{
                                        fontWeight: 600,
                                        fontFamily: 'primaryFont',
                                        color: theme.palette.text.primary,
                                        mb: 2,
                                        textAlign: 'left'
                                    }}
                                >
                                    Product Media
                                </Typography>
                                <DragDropImageUploader
                                    images={productImages}
                                    onChange={handleImageChange}
                                />
                            </Paper>
                        </Grid>

                        {/* Column 2 - Details */}
                        <Grid item xs={12} md={5}>
                            {/* General Information Accordion */}
                            <Accordion defaultExpanded disableGutters elevation={0} sx={accordionSx}>
                                <AccordionSummary expandIcon={<ExpandMore />} sx={accordionSummarySx}>
                                    <Typography
                                        variant='body2'
                                        sx={{
                                            fontWeight: 600,
                                            fontFamily: 'primaryFont',
                                            color: theme.palette.text.primary,
                                            textAlign: 'left'
                                        }}
                                    >
                                        General Information
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {/* Product Name */}
                                        <Box>
                                            <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'left' }}>
                                                Product Name *
                                            </Typography>
                                            <BasicTextField
                                                size='small'
                                                value={productData.name}
                                                onChange={(e) => setProductData({ ...productData, name: e.target.value })}
                                                fullWidth
                                                required
                                                placeholder='Enter product name'
                                            />
                                        </Box>

                                        {/* Category */}
                                        <Box>
                                            <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'left' }}>
                                                Category *
                                            </Typography>
                                            <BasicAutocomplete
                                                name='category'
                                                label='Category'
                                                fullWidth
                                                size='small'
                                                options={uniqueCategories}
                                                value={productData.category || ''}
                                                onChange={(newValue) => {
                                                    setProductData({ ...productData, category: Array.isArray(newValue) ? newValue[0] : newValue });
                                                }}
                                                freeSolo
                                            />
                                        </Box>

                                        {/* Description */}
                                        <Box>
                                            <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'left' }}>
                                                Description
                                            </Typography>
                                            <BasicTextField
                                                size='small'
                                                value={productData.description || ''}
                                                onChange={(e) => setProductData({ ...productData, description: e.target.value })}
                                                fullWidth
                                                multiline
                                                rows={3}
                                                placeholder='Describe your product...'
                                            />
                                        </Box>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>

                            {/* Pricing Accordion */}
                            <Accordion defaultExpanded disableGutters elevation={0} sx={accordionSx}>
                                <AccordionSummary expandIcon={<ExpandMore />} sx={accordionSummarySx}>
                                    <Typography
                                        variant='body2'
                                        sx={{
                                            fontWeight: 600,
                                            fontFamily: 'primaryFont',
                                            color: theme.palette.text.primary,
                                            textAlign: 'left'
                                        }}
                                    >
                                        Pricing
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {/* Base Pricing */}
                                        <Box>
                                            <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'left' }}>
                                                Base Pricing *
                                            </Typography>
                                            <BasicTextField
                                                size='small'
                                                type='number'
                                                value={productData.price}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '') {
                                                        setProductData({ ...productData, price: '' as any });
                                                    } else {
                                                        const numValue = parseFloat(value);
                                                        setProductData({ ...productData, price: isNaN(numValue) ? 0 : numValue });
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                                                        setProductData({ ...productData, price: 0 });
                                                    }
                                                }}
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: <InputAdornment position='start'>{config.currency}</InputAdornment>
                                                }}
                                                sx={{
                                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                                                }}
                                            />
                                        </Box>

                                        {/* Discount Section */}
                                        <Box>
                                            <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'left' }}>
                                                Discount
                                            </Typography>
                                            <ToggleButtonGroup
                                                value={productData.discountType || 'percentage'}
                                                exclusive
                                                onChange={(_, newType) => {
                                                    if (newType !== null) {
                                                        setProductData({ ...productData, discountType: newType, discount: 0 });
                                                    }
                                                }}
                                                size='small'
                                                sx={{ mb: 1.5, width: '100%' }}
                                            >
                                                <ToggleButton value='percentage' sx={{ flex: 1, fontSize: '0.75rem', fontFamily: 'primaryFont', textTransform: 'none' }}>
                                                    Percentage (%)
                                                </ToggleButton>
                                                <ToggleButton value='value' sx={{ flex: 1, fontSize: '0.75rem', fontFamily: 'primaryFont', textTransform: 'none' }}>
                                                    Fixed Amount ({config.currency})
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                            <BasicTextField
                                                size='small'
                                                type='number'
                                                value={productData.discount}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '') {
                                                        setProductData({ ...productData, discount: '' as any });
                                                    } else {
                                                        const numValue = parseFloat(value);
                                                        setProductData({ ...productData, discount: isNaN(numValue) ? 0 : numValue });
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                                                        setProductData({ ...productData, discount: 0 });
                                                    }
                                                }}
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: (productData.discountType || 'percentage') === 'value'
                                                        ? <InputAdornment position='start'>{config.currency}</InputAdornment>
                                                        : undefined,
                                                    endAdornment: (productData.discountType || 'percentage') === 'percentage'
                                                        ? <InputAdornment position='end'>%</InputAdornment>
                                                        : undefined
                                                }}
                                                sx={{
                                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                                                }}
                                            />
                                        </Box>

                                        {/* Cost Price */}
                                        <Box>
                                            <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'left' }}>
                                                Cost Price (For Profit Tracking)
                                            </Typography>
                                        <BasicTextField
                                                size='small'
                                                type='number'
                                                value={productData.costPrice || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '') {
                                                        setProductData({ ...productData, costPrice: undefined });
                                                } else {
                                                        const numValue = parseFloat(value);
                                                        setProductData({ ...productData, costPrice: isNaN(numValue) ? 0 : numValue });
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    if (e.target.value !== '' && isNaN(parseFloat(e.target.value))) {
                                                        setProductData({ ...productData, costPrice: 0 });
                                                    }
                                                }}
                                            fullWidth
                                            InputProps={{
                                                    startAdornment: <InputAdornment position='start'>{config.currency}</InputAdornment>
                                            }}
                                                sx={{
                                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                                            }}
                                        />
                                        </Box>

                                        {/* Pricing Summary */}
                                        {productData.price && productData.price > 0 && (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    pt: 2,
                                                    borderTop: `1px solid ${theme.palette.divider}`
                                                }}
                                            >
                                                <Typography
                                                    variant='caption'
                                                    sx={{
                                                        display: 'block',
                                                        mb: 1.5,
                                                        fontFamily: 'primaryFont',
                                                        color: theme.palette.text.primary,
                                                        fontWeight: 600,
                                                        textAlign: 'left'
                                                    }}
                                                >
                                                    Pricing Summary
                                                </Typography>

                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {/* Original Price */}
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant='caption' sx={{ fontFamily: 'primaryFont', color: theme.palette.text.secondary }}>
                                                                Original Price
                                                            </Typography>
                                                            <Tooltip title='The base price before any discounts are applied' arrow placement='top'>
                                                                <InfoOutlined sx={{ fontSize: 14, color: theme.palette.text.disabled, cursor: 'help' }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Typography variant='caption' sx={{ fontFamily: 'primaryFont', fontWeight: 500 }}>
                                                            {config.currency}{Number(productData.price).toFixed(2)}
                                                        </Typography>
                                                    </Box>

                                                    {/* Discount Row */}
                                                    {(productData.discount ?? 0) > 0 && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Typography variant='caption' sx={{ fontFamily: 'primaryFont', color: theme.palette.text.secondary }}>
                                                                    Discount ({(productData.discountType || 'percentage') === 'percentage'
                                                                        ? `${productData.discount}%`
                                                                        : `${config.currency}${productData.discount}`})
                                                                </Typography>
                                                                <Tooltip
                                                                    title={`${(productData.discountType || 'percentage') === 'percentage'
                                                                        ? 'Percentage off the original price'
                                                                        : 'Fixed amount deducted from the original price'}`}
                                                                    arrow
                                                                    placement='top'
                                                                >
                                                                    <InfoOutlined sx={{ fontSize: 14, color: theme.palette.text.disabled, cursor: 'help' }} />
                                                                </Tooltip>
                                                            </Box>
                                                            <Typography variant='caption' sx={{ fontFamily: 'primaryFont', fontWeight: 500, color: theme.palette.error.main }}>
                                                                -{config.currency}{(productData.discountType || 'percentage') === 'percentage'
                                                                    ? (Number(productData.price) * (Number(productData.discount) / 100)).toFixed(2)
                                                                    : Number(productData.discount).toFixed(2)}
                                            </Typography>
                                                        </Box>
                                        )}

                                                    {/* Final Selling Price */}
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: `1px dashed ${theme.palette.divider}` }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant='caption' sx={{ fontFamily: 'primaryFont', color: theme.palette.text.primary, fontWeight: 600 }}>
                                                                Final Selling Price
                                                            </Typography>
                                                            <Tooltip title='The price customers will pay after discounts' arrow placement='top'>
                                                                <InfoOutlined sx={{ fontSize: 14, color: theme.palette.text.disabled, cursor: 'help' }} />
                                                            </Tooltip>
                                                        </Box>
                                                        <Typography variant='caption' sx={{ fontFamily: 'primaryFont', fontWeight: 700, color: theme.palette.success.main, fontSize: '0.85rem' }}>
                                                            {config.currency}{parseFloat(calculateDiscountedPrice(productData.price, productData.discount, productData.discountType)).toFixed(2)}
                                                        </Typography>
                            </Box>

                                                    {/* Cost & Profit Section */}
                                                    {productData.costPrice && productData.costPrice > 0 && (
                                                        <>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Typography variant='caption' sx={{ fontFamily: 'primaryFont', color: theme.palette.text.secondary }}>
                                                                        Cost Price
                                                                    </Typography>
                                                                    <Tooltip title='Your cost to acquire or produce this product' arrow placement='top'>
                                                                        <InfoOutlined sx={{ fontSize: 14, color: theme.palette.text.disabled, cursor: 'help' }} />
                                                                    </Tooltip>
                                                                </Box>
                                                                <Typography variant='caption' sx={{ fontFamily: 'primaryFont', fontWeight: 500 }}>
                                                                    {config.currency}{productData.costPrice.toFixed(2)}
                                                                </Typography>
                                                            </Box>

                                                            {/* Profit per Unit & Margin Combined */}
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: `1px dashed ${theme.palette.divider}` }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Typography variant='caption' sx={{ fontFamily: 'primaryFont', color: theme.palette.text.primary, fontWeight: 600 }}>
                                                                        Profit
                                                                    </Typography>
                                                                    <Tooltip
                                                                        title='Profit per unit = Final Price - Cost Price. Margin % = (Profit / Final Price) × 100'
                                                                        arrow
                                                                        placement='top'
                                                                    >
                                                                        <InfoOutlined sx={{ fontSize: 14, color: theme.palette.text.disabled, cursor: 'help' }} />
                                                                    </Tooltip>
                                                                </Box>
                                                                {(() => {
                                                                    const finalPrice = parseFloat(calculateDiscountedPrice(productData.price, productData.discount, productData.discountType));
                                                                    const profit = finalPrice - productData.costPrice;
                                                                    const margin = finalPrice > 0 ? ((finalPrice - productData.costPrice) / finalPrice) * 100 : 0;
                                                                    const isPositive = profit >= 0;
                                                                    return (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                                                                variant='caption'
                                    sx={{
                                                                                    fontFamily: 'primaryFont',
                                                                                    fontWeight: 600,
                                                                                    color: isPositive ? theme.palette.success.main : theme.palette.error.main
                                    }}
                                >
                                                                                {isPositive ? '+' : ''}{config.currency}{profit.toFixed(2)}
                                </Typography>
                                <Typography
                                                                                variant='caption'
                                    sx={{
                                                                                    fontFamily: 'primaryFont',
                                                                                    fontWeight: 700,
                                                                                    fontSize: '0.85rem',
                                                                                    color: isPositive ? theme.palette.success.main : theme.palette.error.main,
                                                                                    backgroundColor: isPositive
                                                                                        ? 'rgba(76, 175, 80, 0.1)'
                                                                                        : 'rgba(244, 67, 54, 0.1)',
                                                                                    px: 1,
                                                                                    py: 0.25,
                                                                                    borderRadius: 1
                                                                                }}
                                                                            >
                                                                                {margin.toFixed(1)}%
                                </Typography>
                                                                        </Box>
                                                                    );
                                                                })()}
                                                            </Box>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>

                            {/* Inventory Accordion */}
                            <Accordion defaultExpanded disableGutters elevation={0} sx={accordionSx}>
                                <AccordionSummary expandIcon={<ExpandMore />} sx={accordionSummarySx}>
                                    <Typography
                                        variant='body2'
                                    sx={{
                                            fontWeight: 600,
                                            fontFamily: 'primaryFont',
                                            color: theme.palette.text.primary,
                                            textAlign: 'left'
                                    }}
                                >
                                        Inventory
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {/* Quantity */}
                                        <Box>
                                            <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'left' }}>
                                                Stock On Hand
                                            </Typography>
                                            <BasicTextField
                                                size='small'
                                                type='number'
                                                value={productData.stockOnHand}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '') {
                                                        setProductData({ ...productData, stockOnHand: '' as any });
                                                    } else {
                                                        const intValue = parseInt(value);
                                                        setProductData({ ...productData, stockOnHand: isNaN(intValue) ? 0 : intValue });
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                                                        setProductData({ ...productData, stockOnHand: 0 });
                                                    }
                                                }}
                                                fullWidth
                                                sx={{
                                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                                            }}
                                        />
                                        </Box>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>

                            {/* Product Variants Accordion */}
                            <Accordion disableGutters elevation={0} sx={accordionSx}>
                                <AccordionSummary expandIcon={<ExpandMore />} sx={accordionSummarySx}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                fontFamily: 'primaryFont',
                                                fontWeight: 600,
                                                color: theme.palette.text.primary,
                                                textAlign: 'left'
                                            }}
                                        >
                                            Product Variants
                                        </Typography>
                                        {(productData.variantOptions?.length || 0) > 0 && (
                                            <Box
                                                sx={{
                                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                                                    borderRadius: '12px',
                                                    px: 1,
                                                    py: 0.25,
                                                    fontSize: '0.7rem',
                                                    fontFamily: 'primaryFont',
                                                    fontWeight: 600
                                            }}
                                        >
                                                {productData.variantOptions?.length}
                                            </Box>
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                                    <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{ display: 'block', mb: 2, fontFamily: 'primaryFont', textAlign: 'left' }}
                                    >
                                        Add variants like Size, Color, or Material to offer different options for this product.
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'left' }}>
                                                Variant Type
                                            </Typography>
                                            <BasicAutocomplete
                                                freeSolo
                                                name='variantType'
                                                label='Variant Type'
                                                fullWidth
                                                size='small'
                                                options={uniqueVariants}
                                                value={productData.variantType || ''}
                                                onChange={(newValue) => setProductData({ ...productData, variantType: newValue.toString() })}
                                            />
                                            <Typography variant='caption' sx={{ display: 'block', mt: 0.5, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontSize: '0.7rem', fontStyle: 'italic', textAlign: 'left' }}>
                                                e.g., Size, Color, Material
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant='caption' sx={{ display: 'block', mb: 0.75, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontWeight: 500, textAlign: 'left' }}>
                                                Variant Options
                                        </Typography>
                                        <BasicAutocomplete
                                            freeSolo
                                            multiple
                                                name='variantOptions'
                                                label='Variant Options'
                                            fullWidth
                                                size='small'
                                            options={uniqueVariantOptions}
                                            value={productData.variantOptions || []}
                                            onChange={(newValue) => setProductData({
                                                ...productData,
                                                variantOptions: Array.isArray(newValue) ? newValue : [newValue]
                                            })}
                                            disabled={!productData.variantType}
                                        />
                                            <Typography variant='caption' sx={{ display: 'block', mt: 0.5, fontFamily: 'primaryFont', color: theme.palette.text.secondary, fontSize: '0.7rem', fontStyle: 'italic', textAlign: 'left' }}>
                                                Type and press Enter to add items
                                        </Typography>
                                    </Grid>
                                </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Dialog>
    );
};
